import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Box,
  Button,
  Card,
  CardHeader,
  TextField,
  InputAdornment,
  Fade,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ButtonGroup,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import DevicesIcon from "@mui/icons-material/Devices";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SystemUpdateIcon from "@mui/icons-material/SystemUpdate";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import Pagination from "@mui/material/Pagination";
import { getClients, blockClient, unblockClient, deleteClient, getPartners, getClientsStatusMetrics } from "./api";
import { useAuth } from "./AuthContext";
import { getStatusColor, formatDate } from "./utils";

function ClientList({
  apiKey,
  onClientSelect,
  onRefresh,
  refreshKey,
  onError,
}) {
  const [clients, setClients] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  
  // Filter state - all filters are advanced now
  const [filters, setFilters] = useState({
    name: "",
    clientID: "",
    partnerName: "",
    company: "",
    location: "",
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  
  const [selectedRow, setSelectedRow] = useState(null);

  const [actionInProgress, setActionInProgress] = useState(false);
  const [actionClient, setActionClient] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [actionSuccessMessage, setActionSuccessMessage] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuClientId, setMenuClientId] = useState(null);

  const [copiedId, setCopiedId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const rowsPerPage = 10; // Fetch 10 at a time for better performance

  // Metrics state
  const [metricsData, setMetricsData] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const { isAdmin } = useAuth();
  const canManageClients = Boolean(isAdmin);
  const columnCount = canManageClients ? 10 : 9;

  const getClientId = (client) => {
    return client.id || client.clientID;
  };

  const truncateClientId = (client) => {
    const clientId = getClientId(client);
    if (!clientId) return "";
    if (clientId.length <= 8) return clientId;
    return `${clientId.substring(0, 4)}...${clientId.substring(clientId.length - 4)}`;
  };

  const handleCopyClientId = (event, client) => {
    event.stopPropagation();
    const clientId = getClientId(client);
    navigator.clipboard.writeText(clientId);
    setCopiedId(clientId);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleMenuOpen = (event, clientId) => {
    if (!canManageClients) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuClientId(clientId);
  };

  const handleMenuClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
    setMenuClientId(null);
  };

  const openConfirmDialog = (action, client) => {
    if (!canManageClients) return;
    setConfirmAction(action);
    setActionClient(client);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!canManageClients || !actionClient || !confirmAction) return;

    setActionInProgress(true);
    try {
      const clientId = getClientId(actionClient);

      switch (confirmAction) {
        case "block":
          await blockClient(apiKey, clientId);
          setActionSuccessMessage(
            `Client "${actionClient.name || clientId}" has been blocked.`,
          );
          break;
        case "unblock":
          await unblockClient(apiKey, clientId);
          setActionSuccessMessage(
            `Client "${actionClient.name || clientId}" has been unblocked.`,
          );
          break;
        case "delete":
          await deleteClient(apiKey, clientId);
          setActionSuccessMessage(
            `Client "${actionClient.name || clientId}" has been deleted.`,
          );
          break;
        default:
          break;
      }

      fetchClients(page);
      if (onRefresh) onRefresh();

      setConfirmDialogOpen(false);
      setConfirmAction(null);
      setActionClient(null);

      if (clientId === selectedRow) {
        setSelectedRow(null);
        onClientSelect(null);
      }
    } catch (err) {
      console.error(`Failed to ${confirmAction} client:`, err);
      const errorMsg =
        err.data?.error || err.message || `Failed to ${confirmAction} client.`;
      setError(`Error: ${errorMsg} (Status: ${err.status || "N/A"})`);
      if (onError) onError(errorMsg);
    } finally {
      setActionInProgress(false);
    }
  };

  const fetchPartners = useCallback(async () => {
    if (!apiKey) return;

    try {
      const data = await getPartners(apiKey);
      setPartners(data || []);
    } catch (err) {
      console.error("Failed to fetch partners:", err);
    }
  }, [apiKey]);

  const getPartnerName = (partnerId) => {
    if (!partnerId) return null;
    const partner = partners.find((p) => p.id === partnerId);
    return partner ? partner.name : partnerId;
  };

  const fetchMetrics = useCallback(async () => {
    if (!apiKey) return;

    setMetricsLoading(true);
    try {
      const data = await getClientsStatusMetrics(apiKey);
      setMetricsData(data);
    } catch (err) {
      console.error("Failed to fetch metrics:", err);
      // Don't show error to user, just use fallback counts
      setMetricsData(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [apiKey]);

  const fetchClients = useCallback(
    async (pageNum, filterParams = {}) => {
      if (!apiKey) {
        setError("API Key is required to fetch clients.");
        setClients([]);
        return;
      }

      setLoading(true);
      setError("");

      try {
        // Pass filter parameters to the API
        const data = await getClients(apiKey, pageNum, rowsPerPage, filterParams);

        // Check if response has pagination metadata
        const hasPagination = data && data.pagination;

        let fetchedClients = hasPagination ? data.data : data;

        // Server already sorts, but ensure data is clean
        const clientsData = fetchedClients || [];

        setClients(clientsData);
        if (onError) onError(null);

        // Update pagination metadata
        if (hasPagination) {
          setTotalPages(data.pagination.totalPages);
          setTotalCount(data.pagination.total);
        } else {
          // No pagination in response, means all clients are returned
          setTotalPages(1);
          setTotalCount(clientsData.length);
        }
      } catch (err) {
        console.error("Failed to fetch clients:", err);
        const errorMsg =
          err.data?.error || err.message || "Failed to fetch client list.";
        setError(`Error: ${errorMsg} (Status: ${err.status || "N/A"})`);
        setClients([]);
        if (onError) onError(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [apiKey, onError, rowsPerPage],
  );

  // Debounce all filters to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  // Build filter parameters from current state
  const buildFilterParams = () => {
    const filterParams = {
      status: statusFilter,
    };
    
    // Add filters if they are set
    if (debouncedFilters.name) filterParams.name = debouncedFilters.name;
    if (debouncedFilters.clientID) filterParams.clientID = debouncedFilters.clientID;
    if (debouncedFilters.partnerName) filterParams.partnerName = debouncedFilters.partnerName;
    if (debouncedFilters.company) filterParams.company = debouncedFilters.company;
    if (debouncedFilters.location) filterParams.location = debouncedFilters.location;
    
    return filterParams;
  };

  // Fetch clients when debounced filters, status filter, or page changes
  useEffect(() => {
    setPage(1);
    fetchClients(1, buildFilterParams());
    fetchMetrics();
  }, [fetchClients, fetchMetrics, refreshKey, debouncedFilters, statusFilter]);

  const handleRowClick = (client) => {
    const clientId = getClientId(client);
    setSelectedRow(clientId);
    if (onClientSelect) {
      onClientSelect(clientId);
      setTimeout(() => {
        const el = document.getElementById("client-details-section");
        if (el) {
          const yOffset = -140; // Increased offset for header/tabs height
          const y =
            el.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 100);
    }
  };

  // Server-side filtering is now handled by the API, so no need for client-side filtering
  // Use metrics data for counts (always use metrics as it reflects the full dataset)
  const onlineCount = metricsData?.online ?? 0;
  const offlineCount = metricsData?.offline ?? 0;
  const displayTotalCount = metricsData?.total ?? totalCount;

  return (
    <Card
      elevation={2}
      sx={{ overflow: "hidden", transition: "all 0.3s ease" }}
    >
      <CardHeader
        avatar={<DevicesIcon color="primary" />}
        title={<Typography variant="h6">Clients</Typography>}
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Chip
                label={`${onlineCount} online`}
                color="success"
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${offlineCount} offline`}
                color="default"
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${displayTotalCount} total`}
                color="primary"
                size="small"
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                onRefresh();
                fetchMetrics();
              }}
              disabled={loading || actionInProgress}
              size="small"
            >
              Refresh
            </Button>
          </Box>
        }
        sx={{
          "& .MuiCardHeader-action": {
            alignSelf: "center",
            marginTop: 0,
            marginRight: 1,
          },
        }}
      />

      <Box sx={{ px: 2, pb: 2 }}>
        {/* Filter Section */}
        <Box sx={{ mb: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
              <SearchIcon fontSize="small" />
              Search & Filter Clients
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <ButtonGroup variant="outlined" size="small">
                <Button
                  variant={statusFilter === "" ? "contained" : "outlined"}
                  onClick={() => setStatusFilter("")}
                >
                  All
                </Button>
                <Button
                  variant={statusFilter === "Online" ? "contained" : "outlined"}
                  color={statusFilter === "Online" ? "success" : "inherit"}
                  onClick={() => setStatusFilter("Online")}
                >
                  Online
                </Button>
                <Button
                  variant={statusFilter === "Offline" ? "contained" : "outlined"}
                  onClick={() => setStatusFilter("Offline")}
                >
                  Offline
                </Button>
              </ButtonGroup>
              {Object.values(filters).some(v => v) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setFilters({ name: "", clientID: "", partnerName: "", company: "", location: "" })}
                >
                  Clear All
                </Button>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 2 }}>
            <TextField
              size="small"
              label="Client Name"
              placeholder="e.g., Device-1"
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Client ID"
              placeholder="e.g., abc123"
              value={filters.clientID}
              onChange={(e) => setFilters({ ...filters, clientID: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Partner Name"
              placeholder="e.g., OpeneyeDevices"
              value={filters.partnerName}
              onChange={(e) => setFilters({ ...filters, partnerName: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Company"
              placeholder="e.g., Daves Hot Chicken"
              value={filters.company}
              onChange={(e) => setFilters({ ...filters, company: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Location"
              placeholder="e.g., DHC"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              fullWidth
            />
          </Box>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {actionSuccessMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setActionSuccessMessage("")}
          >
            {actionSuccessMessage}
          </Alert>
        )}

        {!loading && !error && (
          <>
            <TableContainer sx={{ maxHeight: 440, overflow: "auto" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <SignalCellularAltIcon
                          fontSize="small"
                          color="primary"
                        />
                        <Typography variant="subtitle2">Status</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">Deployment</Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <DevicesIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2">Device Name</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <VpnKeyIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2">Client ID</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <GroupIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2">Partner</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">Company</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">Location</Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <MonitorHeartIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2">
                          Last Heartbeat
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <SystemUpdateIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2">
                          Agent Version
                        </Typography>
                      </Box>
                    </TableCell>
                    {canManageClients && (
                      <TableCell align="right">
                        <Typography variant="subtitle2">Actions</Typography>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={columnCount} align="center">
                        <Box
                          sx={{
                            py: 4,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <DevicesIcon
                            sx={{
                              fontSize: 40,
                              color: "text.secondary",
                              mb: 1,
                            }}
                          />
                          {Object.values(filters).some(v => v) || statusFilter ? (
                            <Typography color="textSecondary">
                              No clients match your filters.
                            </Typography>
                          ) : (
                            <Typography color="textSecondary">
                              No clients found.
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {clients.map((client) => {
                    const clientId = getClientId(client);
                    return (
                      <TableRow
                        hover
                        key={clientId}
                        onClick={() => handleRowClick(client)}
                        selected={selectedRow === clientId}
                        sx={{
                          cursor: "pointer",
                          transition: "background-color 0.2s ease",
                          opacity: client.status === "Blocked" ? 0.7 : 1,
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={client.status}
                            color={getStatusColor(client.status)}
                            size="small"
                            sx={{
                              minWidth: 75,
                              fontWeight:
                                client.status === "Online" ? 500 : 400,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={client.deploymentType || "Unknown"}
                            size="small"
                            color={
                              client.deploymentType === "On-Prem"
                                ? "success"
                                : client.deploymentType === "Cloud"
                                ? "info"
                                : "default"
                            }
                            sx={{
                              minWidth: 75,
                              fontWeight: 400,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight:
                                client.status === "Online" ? 500 : 400,
                            }}
                          >
                            {client.name || "(No Name)"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={`Full ID: ${clientId}`}
                            placement="top"
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                component="code"
                                sx={{
                                  fontSize: "0.875rem",
                                  p: 0.5,
                                  bgcolor: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.05)"
                                      : "rgba(0, 0, 0, 0.05)",
                                  borderRadius: "4px",
                                  color: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.85)"
                                      : "rgba(0, 0, 0, 0.87)",
                                }}
                              >
                                {truncateClientId(client)}
                              </Box>
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleCopyClientId(event, client);
                                }}
                                sx={{ ml: 0.5, p: 0.5 }}
                                color={
                                  copiedId === clientId ? "success" : "default"
                                }
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {client.partnerId || client.userGroup ? (
                            <Chip
                              label={getPartnerName(client.partnerId || client.userGroup)}
                              size="small"
                              variant="outlined"
                              color="primary"
                              icon={<GroupIcon />}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {client.company?.name || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {client.location?.name || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDate(client.lastSeen)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {client.agentVersion ? (
                              <>
                                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                  v{client.agentVersion}
                                </Typography>
                                {client.updateAvailable && (
                                  <Tooltip title={`Update available: v${client.latestVersion}`}>
                                    <Chip
                                      label="Update"
                                      size="small"
                                      color="warning"
                                      icon={<NewReleasesIcon />}
                                      sx={{ height: 20, fontSize: "0.7rem" }}
                                    />
                                  </Tooltip>
                                )}
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        {canManageClients && (
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleMenuOpen(event, clientId);
                              }}
                              disabled={actionInProgress}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => {
                    setPage(value);
                    fetchClients(value, buildFilterParams());
                  }}
                  color="primary"
                  showFirstButton
                  showLastButton
                  disabled={loading}
                />
              </Box>
            )}
            {(Object.values(filters).some(v => v) || statusFilter) && (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {clients.length} of {totalCount} matches
                  {totalPages > 1 && ` (Page ${page} of ${totalPages})`}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {canManageClients && (
        <>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={(event) => {
              event.stopPropagation();
              handleMenuClose(event);
            }}
          >
            {menuClientId &&
            clients.find((c) => getClientId(c) === menuClientId)?.status ===
              "Blocked" ? (
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClose(event);
                  openConfirmDialog(
                    "unblock",
                    clients.find((c) => getClientId(c) === menuClientId),
                  );
                }}
              >
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Unblock Client</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClose(event);
                  openConfirmDialog(
                    "block",
                    clients.find((c) => getClientId(c) === menuClientId),
                  );
                }}
              >
                <ListItemIcon>
                  <BlockIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Block Client</ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={(event) => {
                event.stopPropagation();
                handleMenuClose(event);
                openConfirmDialog(
                  "delete",
                  clients.find((c) => getClientId(c) === menuClientId),
                );
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: "error.main" }}>
                Delete Client
              </ListItemText>
            </MenuItem>
          </Menu>

          <Dialog
            open={confirmDialogOpen}
            onClose={() => setConfirmDialogOpen(false)}
            PaperProps={{
              elevation: 3,
              sx: { borderRadius: 2 },
            }}
          >
            <DialogTitle>
              {confirmAction === "delete"
                ? "Confirm Delete"
                : confirmAction === "block"
                  ? "Confirm Block"
                  : "Confirm Unblock"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                {confirmAction === "delete" && (
                  <>
                    Are you sure you want to delete client{" "}
                    <strong>
                      {actionClient?.name || getClientId(actionClient)}
                    </strong>
                    ? This action cannot be undone.
                  </>
                )}
                {confirmAction === "block" && (
                  <>
                    Are you sure you want to block client{" "}
                    <strong>
                      {actionClient?.name || getClientId(actionClient)}
                    </strong>
                    ? The client will be disconnected and unable to reconnect.
                  </>
                )}
                {confirmAction === "unblock" && (
                  <>
                    Are you sure you want to unblock client{" "}
                    <strong>
                      {actionClient?.name || getClientId(actionClient)}
                    </strong>
                    ? The client will be able to reconnect.
                  </>
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button
                onClick={() => setConfirmDialogOpen(false)}
                disabled={actionInProgress}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmAction}
                color={confirmAction === "delete" ? "error" : "primary"}
                variant="contained"
                disabled={actionInProgress}
                startIcon={
                  confirmAction === "delete" ? (
                    <DeleteIcon />
                  ) : confirmAction === "block" ? (
                    <BlockIcon />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
              >
                {confirmAction === "delete"
                  ? "Delete"
                  : confirmAction === "block"
                    ? "Block"
                    : "Unblock"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Card>
  );
}

export default ClientList;
